import { Appointment, Encounter, Location } from 'fhir/r4b';
import { EvolveUser } from 'src/hooks/useEvolveUser';
import {
  allLicensesForPractitioner,
  checkEncounterHasPractitioner,
  EncounterVisitType,
  getEncounterVisitType,
  getTelemedVisitStatus,
  isAppointmentLocked,
  PractitionerLicense,
  StateType,
  TelemedAppointmentStatusEnum,
} from 'utils';
import { AppFlags } from '../stores/contexts/useAppFlags';

export type GetAppointmentAccessibilityDataProps = {
  locationVirtual?: Location;
  encounter: Encounter;
  appointment?: Appointment;
  user?: EvolveUser;
  featureFlags: Partial<AppFlags>;
};

export type GetAppointmentAccessibilityDataResult = {
  allLicenses?: PractitionerLicense[];
  licensedPractitionerStates?: string[];
  state?: StateType;
  isPractitionerLicensedInState: boolean;
  status?: TelemedAppointmentStatusEnum;
  isEncounterAssignedToCurrentPractitioner: boolean;
  isStatusEditable: boolean;
  isAppointmentReadOnly: boolean;
  isCurrentUserHasAccessToAppointment: boolean;
  isAppointmentLocked: boolean;
  visitType: EncounterVisitType;
};

export const getAppointmentAccessibilityData = ({
  locationVirtual,
  encounter,
  appointment,
  user,
  featureFlags = {},
}: GetAppointmentAccessibilityDataProps): GetAppointmentAccessibilityDataResult => {
  const allLicenses = user?.profileResource && allLicensesForPractitioner(user.profileResource);
  const licensedPractitionerStates = allLicenses?.map((item) => item.state);
  const state = locationVirtual?.address?.state as StateType;

  // Bypass licensing and assignment validations for telemed appointments to allow global video call access.
  const isPractitionerLicensedInState = featureFlags.isInPerson
    ? !!state && !!licensedPractitionerStates && licensedPractitionerStates.includes(state as StateType)
    : true;

  const status = getTelemedVisitStatus(encounter.status, appointment?.status);

  const isEncounterAssignedToCurrentPractitioner = featureFlags.isInPerson
    ? !!user?.profileResource && checkEncounterHasPractitioner(encounter, user.profileResource)
    : true;

  const isStatusEditable =
    !!status && ![TelemedAppointmentStatusEnum.complete, TelemedAppointmentStatusEnum.ready].includes(status);

  const isCurrentUserHasAccessToAppointment = featureFlags.isInPerson
    ? isPractitionerLicensedInState &&
      (status === TelemedAppointmentStatusEnum.ready || isEncounterAssignedToCurrentPractitioner)
    : true;

  // Check if appointment is locked via meta tag
  const isAppointmentLockedByMetaTag = appointment ? isAppointmentLocked(appointment) : false;
  const visitType = getEncounterVisitType(encounter);
  const isFollowup = visitType === 'follow-up';

  const isAppointmentReadOnly = (() => {
    if (featureFlags.isInPerson) {
      return isAppointmentLockedByMetaTag && !isFollowup;
    }

    return isAppointmentLockedByMetaTag;
  })();

  return {
    allLicenses,
    licensedPractitionerStates,
    state,
    isPractitionerLicensedInState,
    status,
    isEncounterAssignedToCurrentPractitioner,
    isStatusEditable,
    isAppointmentReadOnly,
    isCurrentUserHasAccessToAppointment,
    isAppointmentLocked: isAppointmentLockedByMetaTag,
    visitType,
  };
};
